// App-wide call manager + context. Mounted once (in Shell) so calls ring on any page.
//
// - Exposes startCall() to the app (chat headers call it).
// - Listens on the shared socket for the ringing lifecycle.
// - Renders the incoming-call prompt and the in-call overlay.
//
// Retailers never mount a caller UI (they can't start calls) and receive on mobile,
// but the code is role-agnostic so a retailer on the web would still get the prompt.
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import type { RootState } from "../../../redux/store";
import type { CallRecord, CallSession, StartCallBody } from "../../../types/call";
import {
  acceptCall as apiAccept,
  cancelCall as apiCancel,
  declineCall as apiDecline,
  endCall as apiEnd,
  startCall as apiStart,
  joinCall as apiJoin,
  redialCall as apiRedial,
} from "../../../redux/apis/callApis";
import { getChatSocket } from "../../../config/chatSocket";
import { decodeSelfId } from "../../../pages/chat/chatHelpers";
import { showErrorToast, showSuccessToast } from "../../../utils/toastUtils";
import { useRingtone } from "./useRingtone";
import IncomingCallModal from "./IncomingCallModal";
import CallOverlay from "./CallOverlay";

interface CallContextValue {
  startCall: (body: StartCallBody) => Promise<void>;
  joinCall: (callId: number) => Promise<void>;
  redialCall: (callId: number, callType?: "voice" | "video") => Promise<void>;
  busy: boolean;
}

const CallContext = createContext<CallContextValue>({
  startCall: async () => {},
  joinCall: async () => {},
  redialCall: async () => {},
  busy: false,
});

export const useCall = () => useContext(CallContext);

export const CallProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const auth = useSelector((s: RootState) => s.auth);
  const isAuthed = !!auth?.isAuthenticated && !!auth?.token;
  const role = auth?.role as string | undefined;
  const selfId = useMemo(() => decodeSelfId(), []);

  const [session, setSession] = useState<CallSession | null>(null);
  const [incoming, setIncoming] = useState<CallRecord | null>(null);
  // Caller is waiting for an answer (no remote in the room yet).
  const [ringingOut, setRingingOut] = useState(false);
  const ringtone = useRingtone();

  // Refs so socket handlers (bound once) see the latest state.
  const sessionRef = useRef<CallSession | null>(null);
  const incomingRef = useRef<CallRecord | null>(null);
  sessionRef.current = session;
  incomingRef.current = incoming;

  const busy = !!session || !!incoming;

  const closeSession = useCallback(() => {
    setSession(null);
    setRingingOut(false);
  }, []);

  const clearIncoming = useCallback(() => {
    setIncoming(null);
    ringtone.stop();
  }, [ringtone]);

  const startCall = useCallback(
    async (body: StartCallBody) => {
      if (sessionRef.current || incomingRef.current) {
        showErrorToast("You're already on a call");
        return;
      }
      try {
        const s = await apiStart(body);
        setSession(s);
        setRingingOut(true);
      } catch (e: any) {
        showErrorToast(e?.response?.data?.message || "Could not start the call");
      }
    },
    []
  );

  // Rejoin an ongoing call from a chat note.
  const joinCall = useCallback(async (callId: number) => {
    if (sessionRef.current || incomingRef.current) {
      showErrorToast("You're already on a call");
      return;
    }
    try {
      const s = await apiJoin(callId);
      setSession(s);
      setRingingOut(false);
    } catch (e: any) {
      showErrorToast(e?.response?.data?.message || "Could not join the call");
    }
  }, []);

  // Redial a past call — rings everyone who was on it again.
  const redialCall = useCallback(async (callId: number, callType?: "voice" | "video") => {
    if (sessionRef.current || incomingRef.current) {
      showErrorToast("You're already on a call");
      return;
    }
    try {
      const s = await apiRedial(callId, callType);
      setSession(s);
      setRingingOut(true);
    } catch (e: any) {
      showErrorToast(e?.response?.data?.message || "Could not start the call");
    }
  }, []);

  const accept = useCallback(async () => {
    const inc = incomingRef.current;
    if (!inc) return;
    ringtone.stop();
    try {
      const s = await apiAccept(inc.id);
      setSession(s);
      setRingingOut(false);
      setIncoming(null);
    } catch (e: any) {
      showErrorToast(e?.response?.data?.message || "Could not join the call");
      setIncoming(null);
    }
  }, [ringtone]);

  const decline = useCallback(async () => {
    const inc = incomingRef.current;
    clearIncoming();
    if (inc) apiDecline(inc.id).catch(() => {});
  }, [clearIncoming]);

  // Hang up / cancel, depending on whether the call was ever answered.
  const hangup = useCallback(() => {
    const s = sessionRef.current;
    closeSession();
    if (!s) return;
    const iStarted =
      String(s.call.initiatorId) === String(selfId) && s.call.initiatorRole === role;
    // A caller still ringing cancels (marks it missed); anything else ends.
    const p = iStarted && ringingOut ? apiCancel(s.call.id) : apiEnd(s.call.id);
    p.catch(() => {});
  }, [closeSession, ringingOut, role, selfId]);

  // ---- socket lifecycle ----
  useEffect(() => {
    if (!isAuthed) return;
    const socket = getChatSocket();

    const onIncoming = (evt: { call: CallRecord }) => {
      // Already busy → politely decline the newcomer.
      if (sessionRef.current || incomingRef.current) {
        apiDecline(evt.call.id).catch(() => {});
        return;
      }
      setIncoming(evt.call);
      ringtone.play();
    };
    const onAccepted = (evt: { callId: number }) => {
      const s = sessionRef.current;
      if (s && s.call.id === evt.callId) setRingingOut(false);
    };
    const onDeclined = (evt: { callId: number; ended?: boolean }) => {
      const s = sessionRef.current;
      if (s && s.call.id === evt.callId && evt.ended) {
        showErrorToast("Call declined");
        closeSession();
      }
    };
    const onCancelled = (evt: { callId: number }) => {
      if (incomingRef.current && incomingRef.current.id === evt.callId) clearIncoming();
    };
    // The call was answered on another device that shares our identity (e.g. another
    // store manager of the same retailer). If we're only RINGING for it (no active
    // session), stop the ringtone and dismiss the incoming popup. The device that
    // actually answered has a session and is unaffected.
    const onTaken = (evt: { callId: number }) => {
      if (incomingRef.current?.id === evt.callId && !sessionRef.current) clearIncoming();
    };
    const onEnded = (evt: { callId: number }) => {
      if (sessionRef.current && sessionRef.current.call.id === evt.callId) closeSession();
      if (incomingRef.current && incomingRef.current.id === evt.callId) clearIncoming();
    };
    // Someone joined/left the call I'm on → a small pop-up.
    const onParticipant = (evt: {
      callId: number;
      joined?: { name: string };
      added?: { name: string };
      left?: { name?: string };
    }) => {
      const s = sessionRef.current;
      if (!s || s.call.id !== evt.callId) return;
      const joined = evt.joined?.name || evt.added?.name;
      if (joined) showSuccessToast(`${joined} joined the call`);
      else if (evt.left?.name) showSuccessToast(`${evt.left.name} left the call`);
    };

    socket.on("call:incoming", onIncoming);
    socket.on("call:accepted", onAccepted);
    socket.on("call:taken", onTaken);
    socket.on("call:declined", onDeclined);
    socket.on("call:cancelled", onCancelled);
    socket.on("call:ended", onEnded);
    socket.on("call:participant", onParticipant);
    return () => {
      socket.off("call:incoming", onIncoming);
      socket.off("call:accepted", onAccepted);
      socket.off("call:taken", onTaken);
      socket.off("call:declined", onDeclined);
      socket.off("call:cancelled", onCancelled);
      socket.off("call:ended", onEnded);
      socket.off("call:participant", onParticipant);
    };
  }, [isAuthed, ringtone, closeSession, clearIncoming]);

  // Did I start this call? (Only the caller may add a third person.)
  const iStarted = useMemo(
    () =>
      !!session &&
      String(session.call.initiatorId) === String(selfId) &&
      session.call.initiatorRole === role,
    [session, selfId, role]
  );

  // Who's on the other side, for the overlay header.
  const peerName = useMemo(() => {
    if (!session) return "";
    if (!iStarted) return session.call.initiatorName;
    if (session.call.scope === "group") return "Group call";
    return session.call.participants[0]?.name || "Calling…";
  }, [session, iStarted]);

  const value = useMemo(
    () => ({ startCall, joinCall, redialCall, busy }),
    [startCall, joinCall, redialCall, busy]
  );

  return (
    <CallContext.Provider value={value}>
      {children}
      {incoming && (
        <IncomingCallModal call={incoming} onAccept={accept} onDecline={decline} />
      )}
      {session && (
        <CallOverlay
          session={session}
          ringing={ringingOut}
          isInitiator={iStarted}
          peerName={peerName}
          onHangup={hangup}
        />
      )}
    </CallContext.Provider>
  );
};
